import { PAYMENT_MAX_CENTS, PAYMENT_MIN_CENTS } from './money-bounds'

/**
 * Splitting a pledge into installments, computed in one place because the page
 * and the server both state the figure and must not be able to disagree.
 *
 * The arithmetic is the total over the count: $180,000 across eighteen months is
 * $10,000 a month. Where it does not divide, the LAST installment absorbs the
 * remainder rather than every one carrying a fraction of a cent — money is
 * integer cents, so somebody has to take the odd ones, and the last is the one
 * that can without changing what anybody agreed to pay each period.
 *
 * `payment_plan` stores the periodic RATE and settles a charge, so the final
 * period takes whatever is left of the debt. That is the same number this
 * returns as `lastCents`, arrived at by the ledger rather than by a stored
 * schedule — there is no table of installments and this invents none.
 */
export function installmentsOf(totalCents: number, installments: number) {
  const rateCents = Math.floor(totalCents / installments)
  const lastCents = totalCents - rateCents * (installments - 1)
  return { rateCents, lastCents }
}

/**
 * What one period actually draws, given the rate, what is behind and what is
 * left of the debt.
 *
 * The rate is a ceiling and never a floor: a draw is capped by the arrears, so
 * somebody who paid ahead is not charged again, and by the debt, so nothing
 * takes more than is owed.
 *
 * **The residue rides on the draw that can carry it**, which is what makes the
 * last real installment the `lastCents` the donor was shown. A total that does
 * not divide leaves odd cents behind every period's rate, and left for a period
 * of their own they are a draw below `PAYMENT_MIN_CENTS` — a figure no card
 * takes, so it declines for ever, the member is mailed a failure every period,
 * and the charge stays open for a cent. A remainder too small to be collected
 * on its own therefore belongs to the draw in hand.
 */
export function installmentDraw(input: {
  rateCents: number
  arrearsCents: number
  owedCents: number
}) {
  const capped = Math.min(input.rateCents, input.arrearsCents, input.owedCents)
  const residue = input.owedCents - capped
  return residue > 0 && residue < PAYMENT_MIN_CENTS ? input.owedCents : capped
}

/**
 * The counts a shul offers before somebody types their own. Plain spans a person
 * thinks in — half a year, a year, two, three — rather than a scale, because the
 * question is how long they want to spread it and not what the figure works out
 * to.
 */
export const SUGGESTED_INSTALLMENTS = [6, 12, 18, 24, 36] as const

/** How far a plan may be stretched. Beyond this it is not a plan, it is a hope. */
export const MAX_INSTALLMENTS = 120

/**
 * Whether the first installment is payable today.
 *
 * Paying it now means it is due now, so a plan that begins later has nothing to
 * take — asking for the card is all such a pledge can do, and the office
 * confirms against it. Computed in one place because the page decides what to
 * offer and the server decides what to charge, and the two disagreeing would
 * either bill somebody early or show an offer that is refused.
 *
 * Both dates are plain civil days and `today` is the caller's, because a civil
 * day is the only thing either end can compare a start date against. The page
 * reads the visitor's; the server reads the ORG's, which is the day the start
 * date was chosen in. No zone here is a safe default for the other: a zone
 * ahead of the org calls tomorrow today and charges a plan that has not
 * started, and one behind it withholds an installment that is due.
 */
export function firstInstallmentIsDue(startsOn: string, today: string) {
  return startsOn <= today
}

/**
 * Whether a proposed split can actually be collected.
 *
 * **The TOTAL is not bounded** — a pledge is undertaken rather than paid, and
 * the single-gift ceiling is about a typo in one card payment. What is bounded
 * is each INSTALLMENT, because each one IS a single payment. Returns the reason
 * in words the person proposing it can act on, or null.
 */
export function whyPlanRefused(
  totalCents: number,
  installments: number,
): string | null {
  if (!Number.isInteger(installments) || installments < 1) {
    return 'Choose how many payments to spread it over.'
  }
  if (installments > MAX_INSTALLMENTS) {
    return `A plan runs to at most ${MAX_INSTALLMENTS} payments.`
  }
  const { rateCents, lastCents } = installmentsOf(totalCents, installments)
  if (rateCents < PAYMENT_MIN_CENTS) {
    return 'That is more payments than the amount divides into. Choose fewer.'
  }
  if (lastCents > PAYMENT_MAX_CENTS) {
    return 'Each payment is larger than a card will take. Choose more payments.'
  }
  return null
}

/**
 * Why a plan of this shape cannot stand, read off what somebody TYPED.
 *
 * A count is typed into a field, and an emptied field is not a plan of zero
 * payments — it is a question with no answer yet. Read as a number it becomes
 * `NaN`, which a bound stated over integers would silently pass; so the string
 * is what this takes, and the form holds its commit and says why instead of
 * quietly turning a plan it is still showing into a single payment.
 *
 * One payment is a legitimate answer and raises no plan, which is why it is not
 * a refusal here and not a toggle beside the field.
 */
export function whyShapeRefused(
  totalCents: number,
  installments: string,
): string | null {
  const count = Number(installments)
  if (installments.trim() === '' || !Number.isInteger(count)) {
    return 'How many payments it is spread over is a whole number, or one for a single payment.'
  }
  if (count < 1) return 'A pledge is settled in one payment or more.'
  if (count === 1) return null
  return whyPlanRefused(totalCents, count)
}
